import { useCanvasStore } from "@/components/Canvas/stores/canvasStore";
import { CameraIcon } from "@radix-ui/react-icons";
import { LibraryIcon } from "lucide-react";
import React, { useCallback, useEffect, useMemo, useRef } from "react";
import { useDispatch } from "react-redux";
import { toast } from "sonner";
import { useMetadataForElements } from "@/hooks/useMetadataForElements";
import { setDraggingComponent } from "@/store/iframeSlice";
import { useCodebaseStore } from "@/store/zustand/codebaseStore";
import { useDarkModeStore } from "@/store/zustand/darkModeStore";
import { usePreviewGeneratorStore } from "@/store/zustand/previewGeneratorStore";
import { type JsxElement, JsxElementType } from "@/utils/jsx/jsxElementTypes";
import { useCanvasId } from "@/utils/utils";
import { setTempoClipboardDataOnDrag } from "../Canvas/utils/canvasClipboardData";
import { BaseCard } from "../Community/CommunityCard";
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuTrigger,
} from "../ui/context-menu";
import { useDragImage } from "./utils";

interface AssetContextMenuProps {
  children: React.ReactNode;
  fullPath: string;
  codebaseId: string;
  variants?: JsxElement[];
  isLibraryComponent?: boolean;
  onAddToLibrary?: () => void;
  onRemoveFromLibrary?: () => void;
}

const AssetContextMenu = React.memo(
  ({
    children,
    fullPath,
    codebaseId,
    variants = [],
    isLibraryComponent = false,
    onAddToLibrary,
    onRemoveFromLibrary,
  }: AssetContextMenuProps) => {
    const handleRegeneratePreview = useCallback(() => {
      // If there are variants, regenerate previews for all variants instead of the main element
      const codebaseIdsToRegenerate =
        variants.length > 0
          ? [
              codebaseId,
              ...variants.map((variant) => variant.codebaseId).filter(Boolean),
            ]
          : [codebaseId];

      const treeElementLookup = useCodebaseStore.getState().treeElementLookup;
      usePreviewGeneratorStore
        .getState()
        .queueElements(
          codebaseIdsToRegenerate
            .map((codebaseId) => treeElementLookup[codebaseId])
            .filter(Boolean),
          true,
        );

      toast.info(`Regenerating preview for ${variants.length} variants`);
    }, [codebaseId, variants]);

    // Check if context menu has any items to show
    const hasMenuItems =
      variants.length > 0 ||
      (!isLibraryComponent && onAddToLibrary) ||
      (isLibraryComponent && onRemoveFromLibrary);

    // If no menu items, just render children without context menu wrapper
    if (!hasMenuItems) {
      return <>{children}</>;
    }

    return (
      <ContextMenu>
        <ContextMenuTrigger>{children}</ContextMenuTrigger>
        <ContextMenuContent className="w-64">
          {variants.length > 0 && (
            <ContextMenuItem
              onClick={(e) => {
                e.stopPropagation();
                handleRegeneratePreview();
              }}
            >
              <span>
                <CameraIcon className="mr-2" />
              </span>
              Generate Previews
            </ContextMenuItem>
          )}
          {!isLibraryComponent && onAddToLibrary && (
            <ContextMenuItem
              onClick={(e) => {
                e.stopPropagation();
                onAddToLibrary();
              }}
            >
              <span>
                <LibraryIcon className="mr-2 w-4 h-4" />
              </span>
              Add to Library
            </ContextMenuItem>
          )}
          {isLibraryComponent && onRemoveFromLibrary && (
            <ContextMenuItem
              onClick={(e) => {
                e.stopPropagation();
                onRemoveFromLibrary();
              }}
            >
              <span>
                <LibraryIcon className="mr-2 w-4 h-4" />
              </span>
              Remove from Library
            </ContextMenuItem>
          )}
        </ContextMenuContent>
      </ContextMenu>
    );
  },
);

interface AssetItemProps {
  element: JsxElement;
  level?: number;
  onExpand?: () => void;
  onSelect?: () => void;
  selected: boolean;
  variants?: JsxElement[];
  variant?: boolean;
  expanded?: boolean;
  draggable?: boolean;
  isLibraryComponent?: boolean;
  onAddToLibrary?: () => void;
  onRemoveFromLibrary?: () => void;
}

export const AssetItem = React.memo(
  ({
    element,
    onSelect,
    selected,
    variants = [],
    draggable = true,
    isLibraryComponent = false,
    onAddToLibrary,
    onRemoveFromLibrary,
  }: AssetItemProps) => {
    const dispatch = useDispatch();
    const canvasId = useCanvasId();

    const elementToUseForImage = useMemo(() => {
      return (element.type === JsxElementType.Component ||
        element.type === JsxElementType.LibraryComponent) &&
        variants?.length
        ? variants[0]
        : element;
    }, [element, variants]);

    const {
      imagePath,
      htmlForInstantUpdate,
      compressedNavTreeForInstantUpdate,
      width,
      height,
    } = useMetadataForElements([elementToUseForImage])[0];

    const variantCount = variants?.length || 0;

    const { setupDragImage, trueDimensions } = useDragImage(
      imagePath || undefined,
      width || undefined,
      height || undefined,
    );

    const handleDragStart = useCallback(
      (e: React.DragEvent) => {
        e.dataTransfer.dropEffect = "move";
        if (!element.codebaseId) return;

        let elementToUse = element;
        if (element.type === JsxElementType.Component && variants?.length) {
          elementToUse = variants[0];
        }

        if (
          elementToUse.type === JsxElementType.StorybookComponent &&
          !elementToUse.storybookSourceStart
        ) {
          toast.error("Could not parse storybook source code");
          return;
        }

        dispatch(setDraggingComponent(elementToUse.type));
        setTempoClipboardDataOnDrag(e, {
          htmlForInstantUpdate: htmlForInstantUpdate || "",
          compressedNavTreeNodesForInstantUpdate:
            compressedNavTreeForInstantUpdate
              ? [compressedNavTreeForInstantUpdate]
              : [],
          insertCodebaseId: elementToUse.codebaseId,
          insertIntoHoveredElement: true,
          width: width || trueDimensions.width || 0,
          height: height || trueDimensions.height || 0,
        });

        // Read zoomPerc imperatively at drag time (no subscription)
        const zoomPerc =
          useCanvasStore.getState().canvasIdToPos[canvasId]?.zoomPerc || 1;
        setupDragImage(e, zoomPerc);
      },
      [
        element,
        variants,
        dispatch,
        canvasId,
        setupDragImage,
        width,
        height,
        trueDimensions.width,
        trueDimensions.height,
        htmlForInstantUpdate,
        compressedNavTreeForInstantUpdate,
      ],
    );

    const handleClick = useCallback(() => {
      onSelect?.();
    }, [onSelect]);

    const path = useMemo(() => {
      return element.filename.split(`${canvasId}/`)[1] || "";
    }, [element.filename, canvasId]);

    const useCanvasDarkMode = useDarkModeStore(
      (state) => state.canvasDarkModeStates[canvasId] || false,
    );

    const containerRef = useRef<HTMLDivElement>(null);

    // Regenerate preview when element becomes visible and has no preview
    useEffect(() => {
      if (!containerRef.current || imagePath) return;

      const observer = new IntersectionObserver(
        (entries) => {
          const entry = entries[0];
          if (entry.isIntersecting) {
            const treeElementLookup =
              useCodebaseStore.getState().treeElementLookup;

            // Queue element and all variants for preview generation
            const codebaseIdsToRegenerate =
              variants.length > 0
                ? [
                    element.codebaseId,
                    ...variants.map((v) => v.codebaseId).filter(Boolean),
                  ]
                : [element.codebaseId];

            const elementsToQueue = codebaseIdsToRegenerate
              .map((id) => treeElementLookup[id])
              .filter(Boolean);

            if (elementsToQueue.length > 0) {
              usePreviewGeneratorStore
                .getState()
                .queueElements(elementsToQueue);
            }

            // Disconnect after queuing to avoid repeated triggers
            observer.disconnect();
          }
        },
        { threshold: 0.1 },
      );

      observer.observe(containerRef.current);

      return () => observer.disconnect();
    }, [element.codebaseId, variants, imagePath]);

    const cardContent = (
      <BaseCard
        title={element.componentName || "Component"}
        titleTooltip={path || undefined}
        imageUrl={imagePath || undefined}
        count={variantCount > 0 ? variantCount : undefined}
        selected={selected}
        onClick={handleClick}
        onDragStart={draggable ? handleDragStart : undefined}
        className="aspect-4/3"
        isThumbnail
        lightBackground={!useCanvasDarkMode}
        isLibraryComponent={isLibraryComponent}
      />
    );

    // Always wrap with context menu - it handles showing/hiding based on available options
    return (
      <div ref={containerRef}>
        <AssetContextMenu
          fullPath={element.filename}
          key={element.key}
          codebaseId={element.codebaseId}
          variants={variants}
          isLibraryComponent={isLibraryComponent}
          onAddToLibrary={onAddToLibrary}
          onRemoveFromLibrary={onRemoveFromLibrary}
        >
          {cardContent}
        </AssetContextMenu>
      </div>
    );
  },
);

AssetItem.displayName = "AssetItem";
